import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Text,
  Select,
  Spinner,
  Td,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Badge,
  Checkbox,
  Button,
  Stack,
  Input,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import FeesTypeModal from "./Edit";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const FeesGroupTable = () => {
  const [feeGroups, setFeeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOption, setSortOption] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null); // For editing
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const toast = useToast();

  // Fetch fee groups when the component is first mounted
  useEffect(() => {
    fetchFeeGroups();
  }, []);

  const fetchFeeGroups = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/admin/Fees/fees_group/list_fee_group.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            org_id: sessionStorage.getItem("cmp_id"),
            user_id: sessionStorage.getItem("user_id"),
          }),
        }
      );
      const result = await response.json();
      console.log("====================================");
      console.log(result);
      console.log("====================================");
      if (result.success) {
        setFeeGroups(result.data);
      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to fetch data. Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Action
  const handleDelete = async (groupId) => {
    const user_id = sessionStorage.getItem("user_id");
    const org_id = sessionStorage.getItem("cmp_id"); // Assuming the organization ID is static or set elsewhere in the app

    try {
      const response = await fetch(
        `${BASE_URL}/admin/Fees/fees_group/delete_fee_group.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            group_id: groupId,
            org_id: org_id,
            user_id: user_id,
          }),
        }
      );
      const result = await response.json();

      if (result.success) {
        // Remove the deleted group from the feeGroups array
        setFeeGroups(feeGroups.filter((group) => group.group_id !== groupId));
        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to delete. Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle Edit Action
  const handleEdit = (group) => {
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  const refreshData = () => {
    // After editing the fee group, refresh the fee groups list
    fetchFeeGroups();
  };

  const filteredFeeGroups = feeGroups.filter((group) => {
    if (sortOption === "group") {
      // Search only in Fee Group Name
      return group.group_name.toLowerCase().includes(searchTerm.toLowerCase());
    } else if (sortOption === "type") {
      // Search only in Fee Types
      return group.fee_types.some((type) =>
        type.type_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true; // Default: No filtering if no search type is selected
  });

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredFeeGroups.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredFeeGroups.length / rowsPerPage);

  return (
    <Box p={6} maxWidth="100%" overflowX="auto">
      <Flex
        direction={{ base: "column", md: "row" }} // FIX: Stack on mobile, row on desktop
        justify="space-between"
        mb={1}
        className="sb"
        p={4}
        align="center"
        gap={4} //  Add vertical gap when stacked on mobile
      >
        <Flex align="center">
          <Text mr={2}>Rows Per Page</Text>
          <Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            width="100px"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Select>
        </Flex>

        <Flex
          align="center"
          direction={{ base: "column", md: "row" }} //  FIX: Stack the inner components
          width={{ base: "100%", md: "auto" }} // FIX: Take full width on mobile
          gap={{ base: 1, md: 0 }}
        >
          <Select
            placeholder="Search by"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            width={{ base: "100%", md: "200px" }} //  FIX: Full width on mobile
            mr={{ base: 0, md: 4 }} //  Remove right margin on mobile, use Flex gap instead
            mb={{ base: 4, md: 0 }}
          >
            <option value="group">Search by Group</option>
            <option value="type">Search by Type</option>
          </Select>

          <Input
            placeholder={
              sortOption === "group"
                ? "Search by Fee Group"
                : sortOption === "type"
                ? "Search by Type"
                : "Select search type"
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            width={{ base: "100%", md: "200px" }} //  FIX: Full width on mobile
            ml={{ base: 0, md: 4 }}
            // width="200px"
            isDisabled={!sortOption}
          />
        </Flex>
      </Flex>

      <Box
        borderWidth="1px"
        borderRadius="md"
        overflowX="auto"
        // overflow="hidden"
      >
        <Table
          sx={{
            "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
          }}
        >
          <Thead>
            <Tr>
              <Th>
                <Checkbox />
              </Th>
              <Th>Fee Group</Th>
              <Th>Description</Th>
              <Th>Fees Type</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Spinner size="xl" />
                </Td>
              </Tr>
            ) : currentData.length > 0 ? (
              currentData.map((group) => (
                <Tr key={group.group_id}>
                  <Td>
                    <Checkbox />
                  </Td>
                  <Td>{group.group_name}</Td>
                  <Td>{group.description}</Td>
                  <Td>
                    {group.fee_types.map((type) => (
                      <Badge key={type.type_id} colorScheme="blue" mr={1}>
                        {type.type_name}
                      </Badge>
                    ))}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme="green"
                      borderRadius={50}
                      fontSize={"x-small"}
                    >
                      Active
                    </Badge>
                  </Td>
                  <Td>
                    <IconButton
                      icon={<EditIcon />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => handleEdit(group)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      size="sm"
                      ml={{ base: 0, md: "2" }}
                      onClick={() => handleDelete(group.group_id)}
                      aria-label="Delete"
                    />
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  No data found.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Flex justify="flex-end" mt={1} className="sb2" p={4}>
        <Button
          onClick={() => setCurrentPage(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>
        {Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            colorScheme={currentPage === i + 1 ? "teal" : "gray"}
            mx={1}
          >
            {i + 1}
          </Button>
        ))}
        <Button
          onClick={() => setCurrentPage(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      {/* Edit Modal */}
      {isModalOpen && (
        <FeesTypeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          initialData={selectedGroup.group_id}
          refreshData={refreshData}
        />
      )}
    </Box>
  );
};

export default FeesGroupTable;
